import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { Contact } from 'src/app/services/messaging/models';
import { AnalyticEventService } from 'src/app/services/analytic-event/analytic-event.service';
import { ContactsService } from 'src/app/services/contacts/contacts.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { OrganizationService } from 'src/app/services/organization/organization.service';
import { UserService } from 'src/app/services/user/user.service';

import { ChannelBrowserComponent } from 'src/app/components/channel-browser/channel-browser.component';
import { CreateChannelModalComponent } from 'src/app/modals/create-channel-modal/create-channel-modal.component';
import { GroupOptionsModalComponent } from 'src/app/modals/group-options-modal/group-options-modal.component';

/**
 * Space-creation flows shared by the sidebar's SPACES menu and the messaging
 * welcome panel: new message (DM/group), new channel, and the public-channel
 * browser. Each opens the corresponding modal and navigates to the resulting
 * conversation; completions are announced on actionCompleted$ (the onboarding
 * checklist listens).
 */
@Injectable({ providedIn: 'root' })
export class ConversationCreateService {
  /** 'conversation_created' | 'channel_created' | 'agent_hired' */
  public readonly actionCompleted$ = new Subject<string>();

  private modalBusy = false;

  constructor(
    private analytics: AnalyticEventService,
    private contactsService: ContactsService,
    private modalService: ModalService,
    private organization: OrganizationService,
    private router: Router,
    private translate: TranslateService,
    private user: UserService,
  ) {}

  public async createDm(): Promise<void> {
    await this.openModal(GroupOptionsModalComponent, 'group-options-modal', {
      title: this.translate.instant('messaging.group.new-title'),
      submitButton: this.translate.instant('messaging.group.create'),
      contacts: this.getContactsWithSelf(),
      createMode: true,
    }, (data) => {
      if (data?.conversationId) {
        this.analytics.logEvent('conversation_created');
        this.actionCompleted$.next('conversation_created');
        this.router.navigate(['messaging'], { queryParams: { conversationId: data.conversationId } });
      }
    });
  }

  public async createChannel(): Promise<void> {
    if (!this.organization.data?.id) return;
    await this.openModal(CreateChannelModalComponent, 'create-channel-modal', {
      organizationId: this.organization.data.id,
      contacts: this.getContactsWithSelf(),
    }, (data) => {
      if (data?.conversationId) {
        this.analytics.logEvent('channel_created');
        this.actionCompleted$.next('channel_created');
        this.router.navigate(['messaging'], { queryParams: { conversationId: data.conversationId } });
      }
    });
  }

  public async browseChannels(userChannelIds: string[]): Promise<void> {
    if (!this.organization.data?.id) return;
    await this.openModal(ChannelBrowserComponent, '', {
      organizationId: this.organization.data.id,
      userChannelIds,
    }, (data) => {
      if (data?.conversationId) {
        this.router.navigate(['messaging'], { queryParams: { conversationId: data.conversationId } });
      }
    });
  }

  public hireAgent(): void {
    // Agents are created via the guided wizard on its own route.
    this.actionCompleted$.next('agent_hired');
    this.router.navigate(['agents/new']);
  }

  private getContactsWithSelf(): Partial<Contact>[] {
    const contacts = this.contactsService.contacts.getValue() || [];
    const self: Partial<Contact> = {
      id: this.user.user?.uid,
      name: this.user.data?.name || this.user.user?.displayName || '',
      avatar: this.user.data?.profilePictureUrl || '',
    };
    return [...contacts, self];
  }

  private async openModal(
    component: any,
    cssClass: string,
    componentProps: Record<string, any>,
    onDismiss?: (data: any) => void,
  ): Promise<void> {
    if (this.modalBusy) return;
    this.modalBusy = true;
    try {
      const modal = await this.modalService.create({
        component,
        ...(cssClass ? { cssClass } : {}),
        componentProps,
      });
      await modal.present();
      const { data } = await modal.onDidDismiss();
      onDismiss?.(data as any);
    } finally {
      this.modalBusy = false;
    }
  }
}
